"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheCelebrationSchedule() {
  const stages = [{
    index: "01",
    name: "THE WELCOME BRUNCH",
    description: "A gentle beginning to our celebration. We gather for a curated seasonal menu, artisanal coffee, and the warmth of shared stories as we anticipate the arrival of Baby Ohlson.",
    duration: "11:00 AM",
    coord: "THE GARDEN ROOM"
  }, {
    index: "02",
    name: "MATERNITY REFLECTIONS",
    description: "A quiet moment to view the journey so far. A curated gallery of maternity portraits and candid memories from the past nine months, displayed with editorial grace.",
    duration: "12:15 PM",
    coord: "THE ARCHIVE HALL"
  }, {
    index: "03",
    name: "WORDS OF WISDOM",
    description: "An invitation for guests to share their hopes and advice. Each message is hand-written on archival cards, to be kept as a legacy for the little one's future library.",
    duration: "12:45 PM",
    coord: "THE WRITING DESK"
  }, {
    index: "04",
    name: "THE TOAST",
    description: "Raising a glass to the expectant parents. A series of short, heartfelt tributes from family and close friends, celebrating the transition into this new chapter of life.",
    duration: "01:15 PM",
    coord: "THE MAIN HEARTH"
  }, {
    index: "05",
    name: "GIFT REGISTRY ACCESS",
    description: "For those wishing to contribute to the nursery, our digital registry remains open. A curated selection of essentials and keepsakes chosen with sustainable care.",
    duration: "ONGOING",
    coord: "DIGITAL PORTAL"
  }, {
    index: "06",
    name: "SWEET FINALE",
    description: "Concluding our afternoon with a selection of botanical-inspired desserts and light refreshments before we bid our guests a fond and grateful farewell.",
    duration: "02:00 PM",
    coord: "THE PATIO"
  }, {
    index: "07",
    name: "THE LEGACY BOOK",
    description: "Following the event, a bound volume of the day's photographs and messages will be created. A permanent record of the love that welcomed Baby Ohlson home.",
    duration: "POST-EVENT",
    coord: "OHLSON RESIDENCE"
  }];
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeStage, setActiveStage] = useState(0);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = Number((entry.target as HTMLElement).dataset.stageIdx);
          setActiveStage(idx);
        }
      });
    }, {
      threshold: 0.5,
      rootMargin: "-20% 0px -40% 0px"
    });
    stageRefs.current.forEach(el => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <section data-section-id="2047" id="the-celebration-schedule" className="bg-background text-foreground py-28 md:py-36 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-14 border-b border-border/40 overflow-hidden flex items-center">
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] font-mono text-xs tracking-[0.18em] text-foreground/70">
          {Array.from({
          length: 6
        }).map((_, i) => <span key={i} data-index={i} className="px-6 flex items-center gap-6 shrink-0">
              <span>WELCOME</span>
              <span className="text-primary"></span>
              <span>BABY OHLSON</span>
              <span className="text-primary"></span>
              <span>EST. 2024</span>
              <span className="text-primary"></span>
              <span>CELEBRATION</span>
              <span className="text-primary"></span>
              <span>SCHEDULE</span>
              <span className="text-primary"></span>
            </span>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="max-w-[1480px] mx-auto px-8 md:px-12 lg:px-16 pt-20 md:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div className="font-mono text-[11px] md:text-[13px] tracking-[0.2em] uppercase text-muted-foreground mb-8">
                § 01 — THE CELEBRATION SCHEDULE / 7 STAGES
              </div>

              {}
              <div className="relative">
                <div aria-hidden="true" className="absolute -top-8 -left-4 md:-left-8 pointer-events-none select-none font-default font-black leading-none text-transparent opacity-[0.05]" style={{
                fontSize: "clamp(12rem, 22vw, 22rem)",
                WebkitTextStroke: "1px var(--foreground)"
              }}>
                  O
                </div>
                <h2 className="relative font-default font-medium leading-[1.1] tracking-tight" style={{
                fontSize: "clamp(2.5rem, 5.5vw, 5.25rem)"
              }}>
                  From anticipation to{" "}
                  <span className="font-serif italic font-normal">arrival</span>.
                </h2>
              </div>

              {}
              <div className="flex items-center gap-3 mt-10 mb-8">
                <div className="h-px flex-1 bg-border" />
                <div className="w-1.5 h-1.5 bg-primary" />
              </div>

              {}
              <p className="font-default text-base md:text-lg leading-[1.6] text-muted-foreground max-w-md">
                Every moment of our baby shower is measured with care — from the first
                brunch toast to the final farewell. We invite you to join us in this 
                intentional sequence of celebration as we prepare for the newest 
                addition to the Ohlson family.
              </p>

              {}
              <div className="mt-12 flex items-center gap-4 font-mono text-xs tracking-[0.18em] text-foreground">
                <span className="text-primary tabular-nums">
                  STAGE {stages[activeStage].index}
                </span>
                <div className="h-px flex-1 bg-border max-w-[120px]" />
                <span className="text-muted-foreground">07</span>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 relative">
            {}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-border" />

            <div className="flex flex-col">
              {stages.map((stage, i) => <StageRow key={stage.index} i={i} stage={stage} isActive={activeStage === i} ref={el => {
              stageRefs.current[i] = el;
            }} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
const StageRow = ({
  i,
  stage,
  isActive,
  ref
}: {
  i: number;
  stage: {
    index: string;
    name: string;
    description: string;
    duration: string;
    coord: string;
  };
  isActive: boolean;
  ref: (el: HTMLDivElement | null) => void;
}) => {
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div data-index={i} data-stage-idx={i} ref={el => {
    ref(el);
    revealRef.current = el;
  }} className={`relative border-t border-border ${i === 6 ? "border-b" : ""} py-10 md:py-14 pl-8 md:pl-12 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      {}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-colors duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${isActive ? "bg-primary" : "bg-border"}`} />

      {}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="font-mono text-[11px] tracking-[0.18em] text-muted-foreground">
          {stage.index} / 07
        </div>
        <div className="font-mono text-[11px] tracking-[0.18em] text-primary">
          {stage.duration}
        </div>
      </div>

      {}
      <div className="flex items-baseline gap-4 md:gap-6 flex-wrap">
        <div className="font-default font-medium leading-none text-foreground tracking-tighter" style={{
        fontSize: "clamp(3rem, 5vw, 4.5rem)"
      }}>
          {stage.index}
        </div>
        <h3 className="font-default font-semibold tracking-wide leading-[1.4] text-foreground" style={{
        fontSize: "clamp(1.25rem, 1.8vw, 1.75rem)"
      }}>
          {stage.name}
        </h3>
      </div>

      {}
      <div className="flex items-center gap-3 mt-6 mb-6">
        <div className="h-px flex-1 bg-border" />
        <div className="w-1.5 h-1.5 bg-primary" />
      </div>

      {}
      <p className="font-default text-[15px] md:text-base leading-[1.6] text-muted-foreground max-w-2xl">
        {stage.description}
      </p>

      {}
      <div className="mt-6 font-mono text-[10px] md:text-[11px] tracking-[0.18em] text-muted-foreground/70">
        {stage.coord}
      </div>
    </div>;
};